import React from 'react';

import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  Text,
  TextTypography,
  ButtonSize,
  Spinner,
  SpinnerTypes,
} from '@wix/wix-ui-tpa/cssVars';
import { StepUndo, useStepIsUnavailable } from '../StepControlsV1';
import { ActionBlockerAppearance } from '../../../../../../../../components-shared/ActionBlocker/ActionBlocker';
import { PreviewActionBlockerContainer } from '../../../../../../../../components-shared/ActionBlocker/PreviewActionBlockerContainer';
import { SidebarLayoutButton } from '../../../../../components/Buttons/SidebarLayoutButton/SidebarLayoutButton';
import { useQuiz } from '../../../../../../../../contexts/Quiz/QuizContext';
import { useSidebarLayoutBase } from '../../../contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { useSettings } from '@wix/tpa-settings/react';
import { useResolveStep } from '../../../../../../../../contexts/ResolveStep/ResolveStepContext';
import utils, { IFollowingEntity, isStepResolved } from '../../../../utils';
import { memberWebAppButtonClick } from '@wix/bi-logger-challenges-member-web/v2';
import { ButtonNames } from '../../../../../../../../contexts/main/biInterfaces';
import { sidebarLayoutDataHooks } from '../../../sidebarLayoutDataHooks';

import { FCWithChildren } from '@wix/challenges-web-library';
import { StepsNavigation } from '../StepControlsV1/components/StepsNavigation';
import challengeSettings from '../../../../../../settingsParams';

import {
  FocusPoint,
  useFocusManager,
} from '../../../../../../../../contexts/FocusManager';
import { useCSSPBEnabled } from '../../../../../../../../hooks/useCSSPBStyle';
import { st, classes } from './FormQuizControls.st.css';
import { SidebarControlsContainer } from '../../../../../components/SidebarControlsContainer/SidebarControlsContainer';

export interface IFormQuizProps {
  isDisabled?: boolean;
  onNextEntity(nextEntity: IFollowingEntity): Promise<void>;
}

export const FormQuizControlsV1: FCWithChildren<IFormQuizProps> = ({
  isDisabled,
  onNextEntity,
}) => {
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const settings = useSettings();
  const alignment = utils.getContentAlignByType(settings, isRTL);
  const {
    controllerIsReady,
    submission,
    sendSubmissionInProgress,
    earnedGrade,
    resubmitTheQuiz,
  } = useQuiz();
  const { currentStep } = useSidebarLayoutBase();
  const { isResolveStepRequestInProgress } = useResolveStep();
  const bi = useBi();
  const cssPBEnabled = useCSSPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });

  const { formQuizRef, formValidity } = useSidebarLayoutBase();

  const isFilled = !!submission;
  const passingGrade = utils.getPassingGrade(currentStep, isFilled);
  const hasPassingGrade = !!passingGrade;
  const isQuizPassed = !hasPassingGrade || earnedGrade >= passingGrade;

  const isUnresolved = !isStepResolved(currentStep);
  const isResubmitAvailable = isFilled && hasPassingGrade && !isQuizPassed;
  const { isSectionLocked, isStepUnavailable } = useStepIsUnavailable();
  const { setNextFocusPoint } = useFocusManager();

  const submit = () => {
    setNextFocusPoint(FocusPoint.Bottom);
    if (formQuizRef?.current) {
      formQuizRef.current?.validate().then(
        (isValid) => {
          if (isValid) {
            void bi.report(
              memberWebAppButtonClick({
                buttonName: ButtonNames.QuizSubmit,
              }),
            );
            void formQuizRef.current?.submitTheForm();
          }
        },
        (err) => {
          console.error("Can't submit the form.", err);
        },
      );
    } else {
      console.error("Can't find the form component.");
    }
  };

  const resubmit = () => {
    void bi.report(
      memberWebAppButtonClick({
        buttonName: ButtonNames.QuizResubmit,
      }),
    );

    resubmitTheQuiz();
  };

  const BaseCTAButton = (
    <PreviewActionBlockerContainer
      appearance={ActionBlockerAppearance.PopoverAppearance}
      content={t('action-blocker.preview-mode.complete-step')}
    >
      {sendSubmissionInProgress || isResolveStepRequestInProgress ? (
        <Spinner type={SpinnerTypes.slim} diameter={40} />
      ) : (
        <SidebarLayoutButton
          className={classes.completeBtn}
          dataHook={sidebarLayoutDataHooks.quizSubmitBtn({
            resubmit: isResubmitAvailable,
          })}
          disabled={
            isDisabled || formValidity?.length > 0 || sendSubmissionInProgress
          }
          fullWidth={isMobile}
          size={isMobile ? ButtonSize.small : ButtonSize.medium}
          onClick={isResubmitAvailable ? resubmit : submit}
        >
          {isResubmitAvailable
            ? t('live-site.step-quiz.retake-btn')
            : t('live-site.step-quiz.complete-btn')}
        </SidebarLayoutButton>
      )}
    </PreviewActionBlockerContainer>
  );

  // eslint-disable-next-line no-nested-ternary
  const msg = isFilled
    ? isQuizPassed
      ? t('live-site.step-quiz.completed-msg')
      : t('live-site.step-quiz.failed-msg')
    : hasPassingGrade
    ? t('live-site.step-quiz.completed-msg-with-score', {
        score: passingGrade,
      })
    : null;
  const msgLine =
    msg && !isResolveStepRequestInProgress ? (
      <Text
        tagName="p"
        typography={TextTypography.runningText}
        className={classes.message}
      >
        {msg}
      </Text>
    ) : null;

  const showBaseButton =
    (isUnresolved || isResubmitAvailable) &&
    !isStepUnavailable &&
    controllerIsReady &&
    !isSectionLocked;
  return (
    <SidebarControlsContainer>
      <div
        className={st(classes.root, {
          mobile: isMobile,
          alignment: !cssPBEnabled ? alignment : null,
          withCSSAlignment: cssPBEnabled,
          rtl: isRTL,
        })}
      >
        {showBaseButton ? BaseCTAButton : null}
        <StepUndo>
          {settings.get(challengeSettings.retakeQuizButtonTxt) as string}
        </StepUndo>
        {msgLine}
        <StepsNavigation
          stepId={currentStep?.id}
          onChangeEntity={onNextEntity}
        />
      </div>
    </SidebarControlsContainer>
  );
};
